<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { ArrowRight } from 'lucide-vue-next'
import { VButton } from '@/modules/shared/components'

const route = useRoute()
const groupName = computed(() => {
  const raw = route.query.group
  const value = Array.isArray(raw) ? raw[0] : raw
  return value?.trim() || null
})
</script>

<template>
  <div class="relative flex min-h-screen flex-col bg-gray-50">
    <!-- Top-left brand (mirrors the onboarding layout) -->
    <div class="pointer-events-none fixed inset-x-0 top-0 z-50 flex items-center justify-between px-6 pt-5">
      <img src="/venture360-logo.svg" class="pointer-events-auto h-6 w-auto" alt="Venture360" />
    </div>

    <!-- Center stage (pulled up — sits in the upper portion of the viewport) -->
    <div class="flex flex-1 justify-center px-6 pb-10 pt-[16vh] text-center sm:pt-[20vh]">
      <div class="mx-auto max-w-[540px]">
        <!-- Horizon visual: a sun setting behind a distant horizon line -->
        <div class="horizon mx-auto mb-2 h-[140px] w-[240px]">
          <svg viewBox="0 0 240 140" width="240" height="140" class="block">
            <defs>
              <linearGradient id="sunGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#009192" />
                <stop offset="100%" stop-color="#0084b4" />
              </linearGradient>
            </defs>

            <!--
              Z-order (SVG: later = on top):
                1. Full sun + glow rings (may extend below horizon)
                2. Ground rect matching page bg (#F9FAFB = tailwind gray-50) — occludes
                   the sun's submerged portion, creating the "setting" illusion.
                3. Reflection lines — drawn on top of the ground so they remain visible.
                4. Horizon line — thin divider on top of everything.
            -->

            <!-- Soft glow rings (full circles, occluded by ground below horizon) -->
            <circle
              class="glow glow-outer"
              cx="120"
              cy="82"
              r="62"
              fill="none"
              stroke="url(#sunGradient)"
              stroke-opacity="0.10"
              stroke-width="1.5"
            />
            <circle
              class="glow glow-mid"
              cx="120"
              cy="82"
              r="50"
              fill="none"
              stroke="url(#sunGradient)"
              stroke-opacity="0.18"
              stroke-width="1.5"
            />

            <!-- Full sun (a real circle; lower half is hidden by the ground) -->
            <circle class="sun" cx="120" cy="82" r="36" fill="url(#sunGradient)" />

            <!-- Ground occluder: must match the page bg color exactly -->
            <rect class="ground" x="0" y="82" width="240" height="58" fill="#F9FAFB" />

            <!-- Reflection lines, fading downward (on top of ground) -->
            <line
              class="reflection reflection-1"
              x1="84"
              y1="97"
              x2="156"
              y2="97"
              stroke="url(#sunGradient)"
              stroke-opacity="0.38"
              stroke-width="1.25"
              stroke-linecap="round"
            />
            <line
              class="reflection reflection-2"
              x1="94"
              y1="110"
              x2="146"
              y2="110"
              stroke="url(#sunGradient)"
              stroke-opacity="0.22"
              stroke-width="1.25"
              stroke-linecap="round"
            />
            <line
              class="reflection reflection-3"
              x1="104"
              y1="122"
              x2="136"
              y2="122"
              stroke="url(#sunGradient)"
              stroke-opacity="0.12"
              stroke-width="1.25"
              stroke-linecap="round"
            />

            <!-- Horizon line (top layer) -->
            <line
              class="horizon-line"
              x1="12"
              y1="82"
              x2="228"
              y2="82"
              stroke="rgba(17,24,39,0.18)"
              stroke-width="1"
              stroke-linecap="round"
            />
          </svg>
        </div>

        <h2 class="title mb-4 text-[32px] font-bold leading-[1.2] tracking-tight text-gray-900">
          Farewell from <span class="gradient-text">Venture360</span>
        </h2>
        <p class="subtitle mx-auto mb-10 max-w-[440px] text-[15px] leading-[1.7] text-gray-500">
          <template v-if="groupName">
            <span class="font-semibold text-gray-700">{{ groupName }}</span> has been permanently closed.
          </template>
          <template v-else> Your group has been permanently closed. </template>
          Thanks for the time and work we got to support.
        </p>

        <div class="cta-wrap inline-block">
          <RouterLink :to="{ name: 'login' }">
            <VButton size="2xl" variant="secondary">
              <span class="flex items-center gap-2">
                Sign in
                <ArrowRight :size="16" :stroke-width="2" />
              </span>
            </VButton>
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.gradient-text {
  background: linear-gradient(90deg, #009192, #0084b4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ─── Entrance animations ─── */
.glow-outer {
  animation: settle 1.4s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both;
}

.glow-mid {
  animation: settle 1.4s cubic-bezier(0.22, 1, 0.36, 1) 0.18s both;
}

.sun {
  animation: settle 1.4s cubic-bezier(0.22, 1, 0.36, 1) 0.26s both;
  transform-origin: 120px 82px;
}

.horizon-line {
  animation: drawLine 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.4s both;
  transform-origin: 120px 82px;
}

.reflection-1 {
  animation: fadeReflection 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.5s both;
}

.reflection-2 {
  animation: fadeReflection 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.6s both;
}

.reflection-3 {
  animation: fadeReflection 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.7s both;
}

.title {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.55s both;
}

.subtitle {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.65s both;
}

.cta-wrap {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.75s both;
}

/*
 * Sun starts fully above the horizon (translated up enough to clear the ground rect)
 * and descends to its half-set resting position. The ground occluder handles the
 * visual "transit" — as the sun moves down, the submerged portion disappears.
 */
@keyframes settle {
  from {
    opacity: 0;
    transform: translateY(-36px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes drawLine {
  from {
    opacity: 0;
    transform: scaleX(0.6);
  }
  to {
    opacity: 1;
    transform: scaleX(1);
  }
}

@keyframes fadeReflection {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .glow-outer,
  .glow-mid,
  .sun,
  .horizon-line,
  .reflection-1,
  .reflection-2,
  .reflection-3,
  .title,
  .subtitle,
  .cta-wrap {
    animation: none;
  }
}
</style>
